// src/Payroll_Management/PayRoll_Report/SalaryHoldEmployeePrint.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Printer, X } from "lucide-react";
import { ThemeContext } from "../../context/ThemeContext";

const BASE_URL = "https://nissi-india.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const field = (v) => (v === null || v === undefined || String(v).trim() === "" ? "" : String(v));

const SalaryHoldEmployeePrint = () => {
  const { isDark } = useContext(ThemeContext);
  const q = useQuery();

  const finYearId = localStorage.getItem("finYearId") || "";
  const empId = q.get("empId") || "";

  const [company, setCompany] = useState({
    name: "",
    address1: "",
    address2: "",
    phone: "",
    email: "",
    logoUrl: "",
  });

  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fallbackLogo = "/logo.png"; // keep in public/logo.png
  const fallbackPhoto = "/noimage.jpg"; // keep in public/noimage.jpg

  const photoUrl = useMemo(() => {
    const filename = employee?.fld_photo;
    if (!filename) return fallbackPhoto;
    return `${BASE_URL}/Employee_photo/${encodeURIComponent(filename)}`;
  }, [employee]);

  const fetchAll = async () => {
    if (!String(finYearId).trim() || !empId) return;

    setLoading(true);
    try {
      const [cRes, eRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/salary-hold-list/company-info`, {
          params: { finYearId: Number(finYearId) },
        }),
        axios.get(`${BASE_URL}/api/salary-hold-list/employee-print-data`, {
          params: { finYearId: Number(finYearId), empId },
        }),
      ]);

      if (cRes?.data?.success) setCompany(cRes.data.company || {});
      if (eRes?.data?.success) {
        setEmployee(eRes.data.employee || null);
        setHistory(eRes.data.history || []);
      } else {
        setEmployee(null);
        setHistory([]);
      }
    } catch {
      setEmployee(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrint = () => window.print();
  const onClose = () => window.close();

  return (
    <div className={`${isDark ? "bg-gray-900 text-white" : "bg-gray-100 text-black"} min-h-screen p-4`}>
      {/* controls */}
      <div className="no-print max-w-6xl mx-auto flex items-center justify-end gap-3 mb-3">
        <button
          onClick={onPrint}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 shadow"
        >
          <Printer className="w-4 h-4" />
          Print
        </button>
        <button
          onClick={onClose}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-700 text-white font-bold hover:bg-gray-800 shadow"
        >
          <X className="w-4 h-4" />
          Close
        </button>
      </div>

      <div className="sheet max-w-6xl mx-auto bg-white text-black p-4 border border-black">
        {loading ? (
          <div className="p-10 text-center">Loading...</div>
        ) : !employee ? (
          <div className="p-10 text-center">No Records Found</div>
        ) : (
          <>
            {/* Header box */}
            <div className="border border-black">
              <div className="grid grid-cols-[180px_1fr] border-b border-black">
                <div className="p-2 border-r border-black flex items-center justify-center">
                  <img
                    src={company.logoUrl}
                    alt="logo"
                    style={{ width: 130, height: 90, objectFit: "contain" }}
                  />
                </div>
                <div className="p-2 text-center">
                  <div className="text-xl font-extrabold">{company.name || "Company Name"}</div>
                  <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
                  <div className="text-xs mt-1 leading-4">
                    {company.address1 ? <div>{company.address1}</div> : null}
                    {company.address2 ? <div>{company.address2}</div> : null}
                    {(company.phone || company.email) ? (
                      <div>
                        {company.phone ? `Phone No ${company.phone}` : ""}
                        {company.phone && company.email ? " | " : ""}
                        {company.email ? company.email : ""}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="text-center font-extrabold py-2 border-b border-black">
                EMPLOYEE MASTER REPORT
              </div>

              {/* Top summary row + photo (like screenshot) */}
              <div className="grid grid-cols-[1fr_180px]">
                <div className="p-2">
                  <table className="w-full text-xs">
                    <tbody>
                      <tr>
                        <td className="w-[140px] font-bold">Branch</td>
                        <td className="w-[10px]">:</td>
                        <td>{field(employee.fld_branchname)}</td>

                        <td className="w-[140px] font-bold">Gross Salary</td>
                        <td className="w-[10px]">:</td>
                        <td>{field(employee.fld_grosssalary)}</td>
                      </tr>

                      <tr>
                        <td className="font-bold">Email ID</td>
                        <td>:</td>
                        <td>{field(employee.fld_emailid)}</td>

                        <td className="font-bold">Daily Bata</td>
                        <td>:</td>
                        <td>{field(employee.fld_dailybata)}</td>
                      </tr>

                      <tr>
                        <td className="font-bold">Mobile Number</td>
                        <td>:</td>
                        <td>{field(employee.fld_mobile)}</td>

                        <td className="font-bold">Special HRA</td>
                        <td>:</td>
                        <td>{field(employee.fld_SpecialHRA)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="p-2 border-l border-black flex items-center justify-center">
                  <img
                    src={photoUrl}
                    alt="employee"
                    style={{ width: 130, height: 150, objectFit: "cover", border: "1px solid #000" }}
                  />
                </div>
              </div>
            </div>

            {/* BASIC DETAILS */}
            <SectionTitle title="BASIC DETAILS" />
            <TwoColTable
              left={[
                ["Employee Name", employee.fld_employee],
                ["Blood Group", ""],
                ["Department", employee.fld_departmentname],
                ["Father Name", employee.fld_fathername],
                ["Gender", employee.fld_gender],
              ]}
              right={[
                ["Employee Code", employee.fld_Employeecode],
                ["Designation", employee.fld_designationname],
                ["Date of Joining", employee.DOJ],
                ["Account Number", employee.fld_accountno],
                ["Bank Name", employee.fld_bankname],
              ]}
            />

            {/* CONTACT DETAILS */}
            <SectionTitle title="CONTACT DETAILS" />
            <TwoColTable
              left={[
                ["Permanent Address", employee.fld_permaddress],
                ["Country", employee.fld_countryname],
                ["District", employee.fld_districtname],
              ]}
              right={[
                ["Temporary Address", employee.fld_tempaddress],
                ["State", employee.fld_statename],
                ["City", employee.fld_cityname],
              ]}
              wideRows={["Permanent Address", "Temporary Address"]}
            />

            {/* PERSONAL DETAILS */}
            <SectionTitle title="PERSONAL DETAILS" />
            <TwoColTable
              left={[
                ["Marital status", employee.fld_maritalstatus],
                ["Date of Birth", employee.DOB],
                ["Qualifications", employee.fld_qualificationname],
                ["Driving License Number", employee.fld_drivinglicnum],
              ]}
              right={[
                ["Pre Experience Year(s)", employee.fld_preexpyrs],
                ["Pre Experience Months", employee.fld_preexpmonths],
                ["Year of Completion", employee.fld_completionyr],
                ["Driving License Expiry Date", employee.expdate || employee.fld_drivinglicexpdate],
              ]}
            />

            {/* STATUS */}
            <SectionTitle title="STATUS" />
            <TwoColTable
              left={[
                ["P.F", employee.fld_pf],
                ["Shift", employee.fld_shiftname],
                ["E.S.I", employee.fld_esi],
                ["P.A.N Number", employee.fld_PANno],
              ]}
              right={[
                ["P.F Number", employee.fld_pfno],
                ["ESI Number", employee.fld_ESIno],
                ["Grade", employee.fld_gradename],
                ["Salary Basis", employee.fld_salarybasisname],
              ]}
            />

            {/* HISTORY TABLE */}
            <div className="mt-3 border border-black">
              <div className="font-bold text-xs px-2 py-1 border-b border-black">EMPLOYEE HISTORY</div>
              <table className="w-full border-collapse text-[11px]">
                <thead>
                  <tr className="border-b border-black">
                    <th className="border-r border-black p-2 text-center">From</th>
                    <th className="border-r border-black p-2 text-center">To</th>
                    <th className="border-r border-black p-2 text-center">Branch</th>
                    <th className="border-r border-black p-2 text-center">Gross</th>
                    <th className="border-r border-black p-2 text-center">PF</th>
                    <th className="border-r border-black p-2 text-center">ESI</th>
                    <th className="border-r border-black p-2 text-center">Net</th>
                    <th className="p-2 text-center">Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-3 text-center">
                        No History
                      </td>
                    </tr>
                  ) : (
                    history.map((h, idx) => (
                      <tr key={idx} className="border-t border-black">
                        <td className="border-r border-black p-2 text-center">{field(h.fld_wrkdperiodfrom)}</td>
                        <td className="border-r border-black p-2 text-center">{field(h.fld_wrkdperiodto)}</td>
                        <td className="border-r border-black p-2 text-center">{field(h.fld_BranchName)}</td>
                        <td className="border-r border-black p-2 text-right">{field(h.fld_grosspay)}</td>
                        <td className="border-r border-black p-2 text-right">{field(h.fld_pf)}</td>
                        <td className="border-r border-black p-2 text-right">{field(h.fld_esi)}</td>
                        <td className="border-r border-black p-2 text-right">{field(h.fld_netpay)}</td>
                        <td className="p-2 text-right">{field(h.fld_bonus)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-4 text-[10px] text-left border border-black">
              <div className="px-2 py-1">
                Report Created by Infraconsoft version 2.1. www.infraconsoft.com
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @page { size: A4 portrait; margin: 10mm; }
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .sheet { border: none !important; padding: 0 !important; }
        }
      `}</style>
    </div>
  );
};

function SectionTitle({ title }) {
  return (
    <div className="mt-3 border border-black">
      <div className="font-bold text-xs px-2 py-1">{title}</div>
    </div>
  );
}

function TwoColTable({ left = [], right = [], wideRows = [] }) {
  const isWide = (label) => wideRows.includes(label);

  return (
    <div className="border border-black border-t-0">
      <table className="w-full border-collapse text-[11px]">
        <tbody>
          {Array.from({ length: Math.max(left.length, right.length) }).map((_, i) => {
            const L = left[i] || ["", ""];
            const R = right[i] || ["", ""];
            return (
              <tr key={i} className="border-t border-black">
                {/* left col */}
                <td className={`p-2 font-bold ${isWide(L[0]) ? "w-[140px]" : "w-[160px]"}`}>{L[0]}</td>
                <td className="p-2 w-[10px]">:</td>
                <td className={`p-2 ${isWide(L[0]) ? "w-[360px]" : ""}`}>{field(L[1])}</td>

                {/* right col */}
                <td className={`p-2 font-bold ${isWide(R[0]) ? "w-[140px]" : "w-[160px]"}`}>{R[0]}</td>
                <td className="p-2 w-[10px]">:</td>
                <td className={`p-2 ${isWide(R[0]) ? "w-[360px]" : ""}`}>{field(R[1])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default SalaryHoldEmployeePrint;